/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";
import { Alert, Box, Tooltip, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import TextField from "@mui/material/TextField";
import { Company, Effect, ModalBase } from "../../utils/Interfaces";
import { selectExistingCompanies } from "./companiesSlice";
import { companyNameExists, editCompany } from "./companiesFunctions";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

export interface EditCompanyModalProps extends ModalBase {
    selectedCompany: Company;
    onCompanyModified: Effect;
}

const EditCompanyModal = (props: EditCompanyModalProps) => {
    const dispatch = useAppDispatch();

    const { open, handleClose, selectedCompany, onCompanyModified } = props;

    const [newCompanyName, setNewCompanyName] = useState<string>("");

    const theme = useTheme();

    const existingCompanies: Company[] = useAppSelector(selectExistingCompanies);

    function canSubmitCompany() {
        let processedCompanyName: string = newCompanyName.trim();
        return (
            !companyNameExists(processedCompanyName, existingCompanies) &&
            processedCompanyName !== selectedCompany.name &&
            processedCompanyName !== ""
        );
    }

    function getErrorMessage(): string {
        let processedCompanyName: string = newCompanyName.trim();
        if (newCompanyName === "") {
            return "The company's name cannot be blank.";
        }

        if (processedCompanyName === selectedCompany.name) {
            return "No changes detected.";
        }

        if (companyNameExists(processedCompanyName, existingCompanies)) {
            return `A company with the name "${processedCompanyName}" already exists.`;
        }

        return "";
    }

    useEffect(() => {
        setNewCompanyName(selectedCompany.name);
    }, [selectedCompany]);

    return (
        <Box>
            <Modal
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                open={open}
                onClose={handleClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <Box
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            border: "1px solid #000",
                            boxShadow: theme.shadows[5],
                            padding: theme.spacing(2, 4, 3),
                            zIndex: 1000,
                            width: 600,
                        }}
                    >
                        <Box component="form" noValidate>
                            {getErrorMessage() !== "" && <Alert severity="error">{getErrorMessage()}</Alert>}
                            {getErrorMessage() === "" && <Alert>Company name is available.</Alert>}

                            <TextField
                                onChange={(event) => {
                                    setNewCompanyName(event.target.value);
                                }}
                                margin="normal"
                                required
                                fullWidth
                                id="name"
                                label="New Company Name"
                                name="name"
                                value={newCompanyName}
                                defaultValue={selectedCompany.name}
                            />
                            <Tooltip title={canSubmitCompany() ? "Submit New Company Name" : "New Company Name is Invalid"}>
                                <Button
                                    onClick={() => {
                                        if (companyNameExists(newCompanyName, existingCompanies)) {
                                            return;
                                        }
                                        editCompany(selectedCompany.id, newCompanyName.trim(), dispatch, onCompanyModified);
                                    }}
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 3, mb: 2 }}
                                    color="primary"
                                    disabled={!canSubmitCompany()}
                                >
                                    Submit
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};

export default EditCompanyModal;
